import { RemoteJobTable } from "@/features/remote_jobs";
import { store } from "@/store";
import { PortfolioView, supabaseApi, useGetCurrentUserQuery, useGetDistinctPortfolioUsersQuery, useGetGroupsQuery, useGetPortfoliosQuery, useGetToolUsageQuery, useIsOwnerQuery } from "@/store/services/supabase";
import { portfolioExportActions } from "@/store/slices/portfolioExport";
import { PortfolioExportProgressTracker } from "@/tools/aggregate/portfolio-export/classes/PortfolioExportProgressTracker";
import { PortfolioExportMaxSize } from "@/tools/aggregate/portfolio-export/components/PortfolioExportMaxSize";
import { PortfolioExportTableHeader } from "@/tools/aggregate/portfolio-export/components/PortfolioExportTableHeader";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { Tool, ToolProps } from "@/types/tools";
import { useKeys } from "@/utils/hooks/useKeys";
import { Avatar } from "@/components/buttons/Avatar";
import { getHazardList } from "@/features/branding";
import { decryptData, encryptData, encryptSecret, getSecretKey } from "@/features/cryptography";
import { Hazard } from "@/features/pe_aldd";
import PortfolioExportFairUseWarning from "@/tools/aggregate/portfolio-export/components/PortfolioExportFairUseWarning";
import { PortfolioExportJobTableBody } from "@/tools/aggregate/portfolio-export/components/PortfolioExportJobTableBody";
import { PortfolioExportPendingKeys } from "@/tools/aggregate/portfolio-export/components/PortfolioExportPendingKeys";
import { PortfolioExportTutorial } from "@/tools/aggregate/portfolio-export/components/PortfolioExportTutorial";
import { PortfolioPasswordInput } from "@/tools/aggregate/portfolio-export/components/PortfolioPasswordInput";
import LargeSiteScreenFairUseWarning from "@/tools/risk-bands/large-site-screen/components/LargeSiteScreenFairUseWarning";
import { KeyPairAndSalt } from "@/types/cryptography";
import { ProcessStatus } from "@/types/processes";
import { arrayBufferToString, hexToArrayBuffer } from "@/utils/crypto";
import { ArrowRightOutlined, Loading3QuartersOutlined, ProjectOutlined } from "@ant-design/icons";
import { useInterval, useLocalStorage } from "@mantine/hooks";
import { useSession } from "@supabase/auth-helpers-react";
import { Alert, AlertProps, Button, Modal, notification, Select, Space, Spin } from "antd";
import { isEqual } from "lodash";
import { useRouter } from "next/router";
import { FC, useEffect, useRef, useState } from "react";
interface PortfolioExportToolProps extends ToolProps {
  mode?: "large-site" | "points";
  tableBodyComponent?: typeof PortfolioExportJobTableBody;
  tableHeaderComponent?: typeof PortfolioExportTableHeader;
}
interface PEAlertProps extends AlertProps {
  key: string;
}
export const PortfolioExportTool = ({
  mode = "points",
  tableBodyComponent = PortfolioExportJobTableBody,
  tableHeaderComponent = PortfolioExportTableHeader,
  ...props
}: PortfolioExportToolProps) => {
  const TableBody = tableBodyComponent;
  const TableHeader = tableHeaderComponent;
  const isLargeSite = mode === "large-site";
  const [keysReceivedState, setKeysReceivedState] = useLocalStorage<"unset" | "awaiting" | "received">({
    key: "orgKeyReceivedState",
    defaultValue: "unset"
  });
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [progressTracker, setProgressTracker] = useState<PortfolioExportProgressTracker>();
  const [error, setError] = useState<null | {
    message: string;
    description: string[];
  }>(null);
  const [jobSchemas, setJobSchemas] = useState<PortfolioExportJobSchema[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alerts, setAlerts] = useState<PEAlertProps[]>([]);
  const [hasJustReceivedKeys, setHasJustReceivedKeys] = useState(false);
  const [showPendingKeysModal, setShowPendingKeysModal] = useState(false);
  const [filteredUserId, setFilteredUserId] = useState("");
  const [filteredGroupId, setFilteredGroupId] = useState("");
  const [queryParamsLoaded, setQueryParamsLoaded] = useState(false);
  const refProgressTracker = useRef<PortfolioExportProgressTracker>();
  const [notify, notificationContext] = notification.useNotification();
  const session = useSession();
  const router = useRouter();
  const [keyPair] = useKeys();
  const {
    data: currentUser,
    refetch: refetchCurrentUser
  } = useGetCurrentUserQuery();
  const {
    data: distinctUsers
  } = useGetDistinctPortfolioUsersQuery({
    isLargeSite
  });
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  const {
    data: groups
  } = useGetGroupsQuery();
  const {
    data: isOwner
  } = useIsOwnerQuery();
  const portfolios = useGetPortfoliosQuery({
    isLargeSite,
    page: currentPage,
    pageSize: PAGE_SIZE,
    userId: filteredUserId,
    groupId: filteredGroupId,
    isPrivate: keysReceivedState === "awaiting"
  }, {
    skip: !queryParamsLoaded
  });
  const keyPairNotPresent = !keyPair.org;

  /** This ensures that we don't have to wait until state update to get updated value. */
  refProgressTracker.current = progressTracker;

  /**
   * Poll for keys every 10 seconds until we have them.
   */
  const updateKeys = async () => {
    if (currentUser?.has_org_keys) {
      refetchCurrentUserInterval.stop();
      return;
    }
    const user = await refetchCurrentUser();
    if (user.data?.has_org_keys) {
      refetchCurrentUserInterval.stop();
      return;
    }
  };
  const refetchCurrentUserInterval = useInterval(updateKeys, 10000);
  useEffect(() => {
    if (!refetchCurrentUserInterval.active && !currentUser?.has_org_keys) {
      refetchCurrentUserInterval.start();
    }
    return refetchCurrentUserInterval.stop;
  }, [refetchCurrentUserInterval]);
  useEffect(() => {
    const {
      userId,
      groupId,
      page
    } = router.query;
    if (typeof userId === "string") setFilteredUserId(userId);
    if (typeof groupId === "string") setFilteredGroupId(groupId);
    if (typeof page === "string") {
      if (page === "1") {
        setCurrentPage(1);
      } else {
        const parsedPage = parseInt(page, 10);
        if (!isNaN(parsedPage)) {
          setCurrentPage(parsedPage);
        }
      }
    }
    setQueryParamsLoaded(true);
  }, [router.query]);
  useEffect(() => {
    const query = {
      ...router.query
    };
    if (filteredUserId) {
      query.userId = filteredUserId;
    } else {
      delete query.userId;
    }
    if (filteredGroupId) {
      query.groupId = filteredGroupId;
    } else {
      delete query.groupId;
    }
    if (currentPage && currentPage !== 1) {
      query.page = String(currentPage);
    } else {
      delete query.page;
    }
    router.replace({
      pathname: router.pathname,
      query
    }, undefined, {
      shallow: true
    });
  }, [filteredUserId, filteredGroupId, currentPage]);
  useEffect(() => {
    const newAlerts: PEAlertProps[] = [];

    // When user previously accessed page without key, but now has them, show notice.
    if (keyPair.user && currentUser?.has_org_keys && (keysReceivedState === "awaiting" || hasJustReceivedKeys)) {
      setHasJustReceivedKeys(true);
      setKeysReceivedState("received");
      newAlerts.push({
        key: "received",
        message: "Keys Received",
        description: <>
            Your organisation&apos;s encryption keys have been successfully received. You can now securely create and
            access shared files with other members of your group.
          </>,
        type: "success",
        showIcon: true,
        closable: !!keyPair.org,
        onClose: () => {
          setHasJustReceivedKeys(false);
        }
      });
    }
    if (keyPair.user && !currentUser?.has_org_keys) {
      // When the user accesses the page without org key, show an awaiting message.
      setKeysReceivedState("awaiting");
      newAlerts.push({
        key: "awaiting",
        message: <Space>
            <span>Awaiting Encryption Keys</span>
            <Spin className="mb-1" indicator={<Loading3QuartersOutlined spin />} size="small" />
          </Space>,
        description: <Space direction="vertical">
            You require your organisation&apos;s encryption keys to enable access to secure files. Please try again
            later or ask an authorised user from your organisation to log in and share the encryption keys. In the
            meantime you can only create & access private files.
            <Button onClick={() => setShowPendingKeysModal(true)} type="link" className="p-0">
              How can I receive my organisation&apos;s encryption keys? <ArrowRightOutlined />
            </Button>
          </Space>,
        type: "info",
        showIcon: true
      });
    }
    setAlerts(newAlerts);
  }, [currentUser, keyPair, keysReceivedState, hasJustReceivedKeys]);
  useEffect(() => {
    props.setIsTutorialDisabled(keyPairNotPresent);
  }, [keyPairNotPresent]);
  useEffect(() => {
    if (!keyPair.user) return;
    const tracker = new PortfolioExportProgressTracker(updateSchema, {
      user: keyPair.user,
      org: keyPair.org
    });
    setProgressTracker(tracker);
  }, [keyPair.user, keyPair.org]);
  useEffect(() => {
    if (!jobSchemas.length || !progressTracker) return;
    progressTracker.schemas = jobSchemas;
    progressTracker.progress().catch(err => {
      if (err.name === "AbortError") return;
      console.error("Error while tracking progress: ", err);
      setError(err);
    });
  }, [jobSchemas, progressTracker]);
  useEffect(() => {
    if (!keyPair.user || !portfolios?.data) {
      setIsLoading(false);
      return;
    }
    processPortfolios();
  }, [portfolios?.data, keyPair.user, keyPair.org]);

  /**
   * Update a specific schema.
   */
  const updateSchema = (updatedSchema: PortfolioExportJobSchema) => {
    setJobSchemas((prev: PortfolioExportJobSchema[]) => prev.map((schema: PortfolioExportJobSchema) => schema.id === updatedSchema.id && !isEqual(schema, updatedSchema) ? updatedSchema : schema));
  };
  const processPortfolios = async () => {
    const {
      user,
      org
    } = keyPair;
    const items = portfolios?.data?.items;
    if (!jobSchemas.length) setIsLoading(true);
    if (!items || !user) {
      setIsLoading(false);
      return;
    }
    try {
      const schemaPromises = items.map(item => transformToJobSchema(item, user, org));
      const results = await Promise.allSettled(schemaPromises);
      const validSchemas = results.filter(isSuccessfulSchema).map(result => result.value).filter(isValidSchema);
      setJobSchemas(validSchemas);
    } catch (err) {
      console.error("Error processing portfolios:", err);
      setError({
        message: "Failed to process portfolios",
        description: [String(err)]
      });
    } finally {
      setIsLoading(false);
    }
  };
  const transformToJobSchema = async (item: PortfolioView, user: KeyPairAndSalt, org?: KeyPairAndSalt | null): Promise<PortfolioExportJobSchema> => {
    const existingSchema = jobSchemas.find(schema => schema.id === item.id);
    const privateKey = item.is_private ? user.keyPair.privateKey : org?.keyPair.privateKey;
    if (!privateKey) {
      throw new Error(`Portfolio ${item.id} requires ${item.is_private ? "user" : "org"} private key`);
    }
    const secret = await getSecretKey(privateKey, item.secret_enc!);
    const name = item.name ? await decryptData(secret, hexToArrayBuffer(item.name)) : "Untitled Portfolio Export";
    if (existingSchema && isCompletedStatus(existingSchema.status)) {
      const hasChanges = existingSchema.groupName !== item.group_name || existingSchema.name !== name || existingSchema.isPrivate !== item.is_private || existingSchema.isSummaryGenerated !== item.is_summary_generated || existingSchema.summaryId !== item.summary_id;
      if (!hasChanges) {
        return existingSchema;
      }
    }

    // Decrypt portfolio data
    const metadata = item.metadata ? JSON.parse(await decryptData(secret, hexToArrayBuffer(item.metadata))) : undefined;
    return {
      id: item.id!,
      name,
      assets: {
        errorCount: item.error,
        totalCount: item.total,
        processedCount: item.success,
        unprocessedCount: item.unprocessed
      },
      startTime: new Date(item.created_at!).getTime(),
      status: getJobStatus(item, existingSchema),
      summaryId: item.summary_id || "",
      userId: item.user_id || "",
      email: item.email || "Unknown User",
      groupId: item.group_id || "",
      groupName: item.group_name || "",
      secret,
      isLargeSite,
      isPrivate: item.is_private || false,
      isSummaryGenerated: item.is_summary_generated,
      stamps: item.stamps || [],
      metadata,
      incorporatedHazards: "incorporated_hazards" in item && item.incorporated_hazards ? item.incorporated_hazards as Hazard[] : getHazardList().fallback
    };
  };
  const isSuccessfulSchema = (result: PromiseSettledResult<PortfolioExportJobSchema>): result is PromiseFulfilledResult<PortfolioExportJobSchema> => {
    // exclude errored portfolios; presumably could not be decrypted
    return result.status === "fulfilled";
  };
  const isValidSchema = (schema: PortfolioExportJobSchema): boolean => {
    // Only include completed portfolios for other users
    // to avoid multiple batch uploads & progress connection
    return schema.userId === session?.user.id || isCompletedStatus(schema.status);
  };
  const isCompletedStatus = (status: string): boolean => {
    return ["completed", "success", "completed_with_errors"].includes(status);
  };
  const getJobStatus = (item: PortfolioView, existingSchema?: PortfolioExportJobSchema): ProcessStatus => {
    if (item.pending === 0 && item.error) return "completed_with_errors";
    if (item.pending === 0) return "completed";
    if (item.total === item.success + item.error && item.total > 0) return "completed";
    if (existingSchema?.status) return existingSchema.status;
    if (item.unprocessed) {
      store.dispatch(portfolioExportActions.setNewRunTotalInBatchTo({
        id: item.id!,
        count: item.unprocessed
      }));
      return "uploading";
    }
    return "processing";
  };
  return <>
      {process.env.NEXT_PUBLIC_THEME_NAME?.startsWith("hkma") && <PortfolioExportMaxSize />}
      {notificationContext}

      <Modal closable onCancel={() => setShowPendingKeysModal(false)} width={540} open={showPendingKeysModal} footer={null} data-sentry-element="Modal" data-sentry-source-file="index.tsx">
        <PortfolioExportPendingKeys data-sentry-element="PortfolioExportPendingKeys" data-sentry-source-file="index.tsx" />
      </Modal>

      {alerts.map(({
      key,
      ...alert
    }) => <Alert key={key} className="mb-6 p-4" {...alert} />)}

      {error && <Alert className="mb-6 p-4" message={error.message || "An unexpected error has occurred"} description={error.description ? <ul>
                {error.description.map((err, index) => <li key={index}>{err}</li>)}
              </ul> : "Please refresh your browser & try again. If the error persists, please contact support."} type="error" closable onClose={() => setError(null)} />}

      {keyPair.user ? <>
          {currentUser?.has_org_keys && !keyPair.org ? <PortfolioPasswordInput prompt={hasJustReceivedKeys ? "You have been granted your organisation’s encryption keys. Verify your password to confirm the exchange." : undefined} isLoading={keyPair.isLoading} /> : <RemoteJobTable<PortfolioExportJobSchema> headerComponent={TableHeader as FC} headerProps={{
        actionBtns: <>
                    <div className="flex items-center justify-start gap-2">
                      {distinctUsers && <Select className="w-max" placeholder="All Users" onChange={id => {
              setCurrentPage(1);
              setFilteredUserId(id);
            }} popupMatchSelectWidth={false} defaultValue={""} value={filteredUserId} options={[{
              value: "",
              label: "All users"
            }, ...distinctUsers.map(u => ({
              value: u.id,
              label: <div className="flex items-center gap-2">
                                  <div>
                                    <Avatar name={u.email} size={16} />
                                  </div>
                                  <span>{u.email}</span>
                                </div>
            }))]} />}
                      {isOwner && groups && <Select className="w-max" placeholder="All groups" onChange={id => {
              setCurrentPage(1);
              setFilteredGroupId(id);
            }} value={filteredGroupId} popupMatchSelectWidth={false} defaultValue={""} options={[{
              value: "",
              label: "All groups"
            }, ...groups.map(g => ({
              value: g.id,
              label: <div className="flex items-center gap-2">
                                  <span>{g.name}</span>
                                </div>
            }))]} />}
                    </div>
                  </>
      }} jobSchemas={jobSchemas} onRunStart={console.log}>
              {toolUsage && (isLargeSite ? <LargeSiteScreenFairUseWarning data={toolUsage} /> : <PortfolioExportFairUseWarning data={toolUsage} />)}
              <TableBody schemas={jobSchemas} totalSchemas={portfolios.data?.count || portfolios.data?.items.length || 0} currentPage={currentPage} onPageChange={page => {
          setIsLoading(true);
          setCurrentPage(page);
        }} pageSize={PAGE_SIZE} loading={portfolios.isLoading} decrypting={isLoading} updateSchema={updateSchema} onDelete={async (id: string) => {
          const action = supabaseApi.endpoints.deletePortfolio.initiate(id);
          await store.dispatch(action);
        }} onEdit={async props => {
          if (!keyPair.user) return;
          let encryptedSecret = "";
          if (props.isPrivate) {
            encryptedSecret = arrayBufferToString(await encryptSecret({
              publicKey: keyPair.user.keyPair.publicKey,
              secret: props.schema.secret.secretAndSalt
            }));
          } else {
            if (keyPair.orgSecret && keyPair.org?.keyPair.publicKey) {
              encryptedSecret = arrayBufferToString(await encryptSecret({
                publicKey: keyPair.org.keyPair.publicKey,
                secret: props.schema.secret.secretAndSalt
              }));
            }
          }
          const details = {
            id: props.schema.id,
            name: await encryptData(props.schema.secret, props.name),
            groupId: props.groupId,
            isPrivate: props.isPrivate,
            secretEnc: encryptedSecret
          };
          const action = supabaseApi.endpoints.updatePortfolioDetails.initiate(details);
          const res = await store.dispatch(action);
          if ("error" in res) {
            console.error(res.error);
            notify.error({
              message: "Error moving portfolio",
              description: "message" in res.error ? res.error.message : "Something went wrong. Please try again."
            });
          } else {
            notify.success({
              message: "Portfolio updated!"
            });
          }
        }} onError={async (err, portfolioId) => {
          const action = supabaseApi.endpoints.deletePortfolio.initiate(portfolioId);
          await store.dispatch(action);
          setError({
            message: "Error while uploading your portfolio",
            description: [...err]
          });
        }} />
            </RemoteJobTable>}
        </> : <PortfolioPasswordInput isLoading={keyPair.isLoading} />}

      {!isLargeSite && <PortfolioExportTutorial onClose={() => props.setIsTutorialOpen(false)} open={props.isTutorialOpen} setState={
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {}} />}
    </>;
};
export default {
  id: "portfolio-insights",
  category: "comprehensive",
  keyPrefix: "comprehensive.portfolioInsights",
  icon: <ProjectOutlined />,
  render: (props: PortfolioExportToolProps) => <PortfolioExportTool {...props} />,
  hasTutorial: true,
  showUsage: true,
  maxWidth: "1440px"
} as Tool;